import React, { useMemo, useState, useCallback } from 'react';
import ReactFlow, {
	ConnectionLineType,
	useNodesState,
	useEdgesState,
	Controls,
	Background,
} from 'reactflow';
import { ReactFlowProvider } from 'reactflow';
import Container from '@amzn/awsui-components-console/container';
import Header from '@amzn/awsui-components-console/header';
import Grid from '@amzn/awsui-components-console/grid';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Button from '@amzn/awsui-components-console/button';
import {
	generateGraphForDisplay,
	getLayoutedElements,
	nodeTypes,
} from 'src/components/createWorklow/updateWFDefinitionCanvas/updateWFDefinitionCanvasUtils';
import { GraphLayoutDirection } from 'src/constants/selfServe/executionViewConstants';
import { TaskDefinition } from 'src/stores/workflowDefinitionCanvasStore/workflowDefinitionCanvasStore.interface';
import { Dependancy } from 'src/stores/workflowDefinitionCanvasStore/workflowDefinitionCanvasStore.interface';
import { ReactFlowHelpNodeIDList } from 'src/components/createWorklow/customNode/customNodeEnums';
import TaskDetailsReviewPanel from 'src/components/createWorklow/taskDetailsReviewPanel/taskDetailsReviewPanel';
import 'src/components/createWorklow/updateWFDefinitionReviewStep/updateWFDefinitionReviewStep.scss';

export interface ViewWorkflowDesignerCanvasProps {
	taskDefinitions: TaskDefinition[];
	dependencies: Dependancy[];
	containerId?: string;
}

export default function ViewWorkflowDesignerCanvas({
	taskDefinitions,
	dependencies,
	containerId,
}: ViewWorkflowDesignerCanvasProps) {
	const { layoutedEdges, layoutedNodes } = useMemo(() => {
		return generateGraphForDisplay(taskDefinitions, dependencies);
	}, [taskDefinitions, dependencies]);

	const [nodes, setNodes, onNodesChange] = useNodesState(layoutedNodes);
	const [edges, setEdges, onEdgesChange] = useEdgesState(layoutedEdges);
	const [taskDetails, setTaskDetails] = useState<TaskDefinition | undefined>();
	const [showTaskDetails, setShowTaskDetails] = useState(false);
	const [reactFlowInstance, setReactFlowInstance] = useState<ReturnType<typeof Object> | null>(null);

	const onLayout = useCallback(
		(direction: GraphLayoutDirection) => {
			const { nodes: layoutedNodes, edges: layoutedEdges } =
				getLayoutedElements(nodes, edges, direction);

			setNodes([...layoutedNodes]);
			setEdges([...layoutedEdges]);

			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					if (reactFlowInstance) {
						reactFlowInstance.fitView();
					}
				});
			});
		},
		[nodes, edges, setNodes, setEdges, reactFlowInstance]
	);

	const showTaskDetailsCallback = useCallback(
		(task?: TaskDefinition) => {
			setTaskDetails(task);
			setShowTaskDetails(Boolean(task));
		},
		[]
	);

	return (
		<Container
			id={containerId}
			header={
				<Header
					variant="h2"
					actions={
						<SpaceBetween direction="horizontal" size="xs">
							<Button
								iconName="view-horizontal"
								variant="icon"
								onClick={() => onLayout(GraphLayoutDirection.TopToBottom)}
								ariaLabel="Vertical Layout"
							>
								Vertical
							</Button>
							<Button
								iconName="view-vertical"
								variant="icon"
								onClick={() => onLayout(GraphLayoutDirection.LeftToRight)}
								ariaLabel="Horizontal Layout"
							>
								Horizontal
							</Button>
						</SpaceBetween>
					}
				>
					Workflow Designer
				</Header>
			}
		>
			<Grid
				gridDefinition={[
					{ colspan: showTaskDetails ? 8 : 12 },
					{ colspan: showTaskDetails ? 4 : 0 },
				]}
			>
				<ReactFlowProvider>
					<div className="reviewCanvas">
						<ReactFlow
							nodes={nodes}
							edges={edges}
							onNodesChange={onNodesChange}
							onEdgesChange={onEdgesChange}
							connectionLineType={ConnectionLineType.SmoothStep}
							fitView
							minZoom={0.1}
							maxZoom={1.5}
							onInit={(instance) => {
								setReactFlowInstance(instance);
								onLayout(GraphLayoutDirection.TopToBottom);
							}}
							onNodeClick={(_, { data }) => {
								if (
									!(
										data.taskDefinition.taskId ===
											ReactFlowHelpNodeIDList.START ||
										data.taskDefinition.taskId ===
											ReactFlowHelpNodeIDList.END
									)
								) {
									showTaskDetailsCallback(data.taskDefinition);
								}
							}}
							onPaneClick={() => {
								showTaskDetailsCallback();
							}}
							nodeTypes={nodeTypes}
							panOnScroll
							elevateEdgesOnSelect
							nodesDraggable={false}
							nodesConnectable={false}
							elementsSelectable={false}
						>
							<Controls />
							<Background color="#aaa" gap={16} />
						</ReactFlow>
					</div>
				</ReactFlowProvider>
				{showTaskDetails && taskDetails && (
					<div className="taskDetailsCntr">
						<TaskDetailsReviewPanel
							taskDetails={{
								...taskDetails,
							}}
							showTaskDetails={() => showTaskDetailsCallback()}
						/>
					</div>
				)}
			</Grid>
		</Container>
	);
}
